import React, { useEffect, useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { type CredentialResponse } from "@react-oauth/google";
import { useAuth } from "@/context/AuthContext";
import AuthLayout from "@/components/auth/AuthLayout";
import AuthField from "@/components/auth/AuthField";
import AuthAlert from "@/components/auth/AuthAlert";
import AuthGoogleButton from "@/components/auth/AuthGoogleButton";
import { AuthMiniQuote } from "@/components/auth/AuthProof";
import {
    getEmailError,
    getLoginNotice,
    SUPPORT_EMAIL,
    CONTACT_URL,
} from "@/components/auth/authCopy";

export default function Login() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { signIn, signInWithGoogle, isAuthenticated } = useAuth();

    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [isLoading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(() =>
        getLoginNotice(searchParams.get("reason")),
    );
    const [emailError, setEmailError] = useState<string | null>(null);
    const [passwordError, setPasswordError] = useState<string | null>(null);

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated) navigate("/");
    }, [isAuthenticated, navigate]);

    const checkEmail = () => {
        const message = getEmailError(email);
        setEmailError(message);
        return message === null;
    };

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault();
        setError(null);

        let ok = checkEmail();
        if (!password) {
            setPasswordError("Type your password first.");
            ok = false;
        }
        if (!ok) return;

        setLoading(true);
        try {
            await signIn(email.trim(), password);
        } catch {
            setError("That email and password don't match an account. Check them and try again.");
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleSuccess = async (res: CredentialResponse) => {
        if (!res.credential) return;
        try {
            await signInWithGoogle(res.credential);
            navigate("/");
        } catch (err) {
            console.error("Backend auth failed:", err);
            setError("We couldn't sign you in with Google. Try again, or use your password.");
        }
    };

    if (isAuthenticated) return null;

    return (
        <AuthLayout
            page="signin"
            title="Welcome back."
            subtitle="Sign in to see what AI is saying about your business."
        >
            <AuthGoogleButton
                text="signin_with"
                onSuccess={handleGoogleSuccess}
                onError={() =>
                    setError("We couldn't sign you in with Google. Try again, or use your password.")
                }
            />

            <div className="auth-or" aria-hidden="true">
                OR
            </div>

            <form onSubmit={handleLogin} noValidate>
                <AuthField
                    fieldClassName="auth-field--email"
                    label="Email"
                    type="email"
                    name="email"
                    autoComplete="email"
                    placeholder="you@yourcompany.com"
                    mobilePlaceholder="Email"
                    value={email}
                    error={emailError}
                    onChange={(e) => {
                        setEmail(e.target.value);
                        setEmailError(null);
                    }}
                    onBlur={() => {
                        if (email.trim()) checkEmail();
                    }}
                />

                <AuthField
                    fieldClassName="auth-field--password"
                    label="Password"
                    type="password"
                    name="password"
                    autoComplete="current-password"
                    placeholder="Your password"
                    mobilePlaceholder="Password"
                    reveal
                    value={password}
                    error={passwordError}
                    onChange={(e) => {
                        setPassword(e.target.value);
                        setPasswordError(null);
                    }}
                />

                {error ? <AuthAlert>{error}</AuthAlert> : null}

                <div className="auth-row-between auth-row-between--end d-only">
                    <Link className="auth-link" to="/forgot-password">
                        Forgot password?
                    </Link>
                </div>

                <button className="auth-btn-primary" type="submit" disabled={isLoading}>
                    {isLoading ? "Signing in…" : "Sign in"}
                </button>
            </form>

            <p className="auth-alt d-only">
                New here? <Link to="/sign-up">Create your free account</Link>
            </p>

            <div className="auth-row-between auth-links-row m-only">
                <Link className="auth-link" to="/forgot-password">
                    Forgot password?
                </Link>
                <Link className="auth-link" to="/sign-up">
                    Create account
                </Link>
            </div>

            <AuthMiniQuote />

            <div className="auth-help d-only">
                <span className="q">Can't get in? We'll help.</span>
                <span className="a">
                    Email <a href={`mailto:${SUPPORT_EMAIL}`}>{SUPPORT_EMAIL}</a> or{" "}
                    <a href={CONTACT_URL} target="_blank" rel="noopener noreferrer">
                        send us a message
                    </a>
                    . A real person answers.
                </span>
            </div>
        </AuthLayout>
    );
}
